import { useCallback, useEffect, useState } from 'react';
import {
  scenarioPlanningApi,
  type EnterpriseDigitalTwin,
  type Scenario,
  type ScenarioConstraint,
  type ScenarioDashboard,
  type ScenarioRisk,
  type ScenarioTemplate,
  type SimulationRecommendation,
  type SimulationResult,
} from '../../api/scenarioplanning';

type Tab = 'dashboard' | 'scenarios' | 'twin' | 'simulation' | 'constraints' | 'risks' | 'recommendations' | 'templates';
type Theme = 'light' | 'dark';

function fmtMoney(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function ScenarioPlanningPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<ScenarioDashboard | null>(null);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [selected, setSelected] = useState<Scenario | null>(null);
  const [simResult, setSimResult] = useState<SimulationResult | null>(null);
  const [twin, setTwin] = useState<EnterpriseDigitalTwin | null>(null);
  const [constraints, setConstraints] = useState<ScenarioConstraint[]>([]);
  const [risks, setRisks] = useState<ScenarioRisk[]>([]);
  const [recommendations, setRecommendations] = useState<SimulationRecommendation[]>([]);
  const [templates, setTemplates] = useState<ScenarioTemplate[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, sc, dt, tmpl] = await Promise.all([
        scenarioPlanningApi.dashboard(),
        scenarioPlanningApi.listScenarios(),
        scenarioPlanningApi.getDigitalTwin(),
        scenarioPlanningApi.listTemplates(),
      ]);
      setDashboard(dash);
      setScenarios(sc);
      setTwin(dt);
      setTemplates(tmpl);
      const sel = sc[0] ?? null;
      setSelected(sel);
      if (!sel) return;
      const [cons, rsk, recs] = await Promise.all([
        scenarioPlanningApi.listConstraints(sel.id),
        scenarioPlanningApi.listRisks(sel.id),
        scenarioPlanningApi.listRecommendations(sel.id),
      ]);
      setConstraints(cons);
      setRisks(rsk);
      setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load scenario planning');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const runSimulation = async () => {
    if (!selected) return;
    setError(null);
    try {
      const result = await scenarioPlanningApi.simulate(selected.id);
      setSimResult(result);
      setTab('simulation');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Simulation failed');
    }
  };

  const shell = dark ? 'min-h-screen bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50 text-slate-900';
  const card = dark ? 'rounded-xl border border-slate-800 bg-slate-900 p-5' : 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm';
  const header = dark ? 'border-b border-slate-800 bg-slate-900' : 'border-b border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-600';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'scenarios', label: 'Scenario Builder' },
    { id: 'twin', label: 'Digital Twin' },
    { id: 'simulation', label: 'Simulation Viewer' },
    { id: 'constraints', label: 'Constraints' },
    { id: 'risks', label: 'Risk Explorer' },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'templates', label: 'Scenario Library' },
  ];

  return (
    <div className={shell}>
      <div className={`${header} px-6 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Enterprise Scenario Planning</h1>
            <p className={`mt-1 text-sm ${muted}`}>Digital twin, cross-functional simulation, what-if analysis, and strategic decision intelligence</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800' : 'bg-slate-100'}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
            <button type="button" onClick={runSimulation} disabled={!selected} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">Simulate</button>
            <button type="button" onClick={refresh} className="rounded-lg bg-slate-600 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700">Refresh</button>
          </div>
        </div>
        {selected && (
          <p className={`mt-2 text-sm ${muted}`}>
            {selected.name} · Risk {selected.composite_risk_score.toFixed(0)} · Confidence {selected.planning_confidence_score.toFixed(0)} · Readiness {selected.enterprise_readiness_score.toFixed(0)}
          </p>
        )}
      </div>
      {error && <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300" role="alert">{error}</div>}
      <div className={`${header} px-6`}>
        <nav className="flex gap-1 overflow-x-auto" role="tablist">
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${tab === t.id ? 'border-violet-600 text-violet-600' : 'border-transparent text-slate-500'}`}>{t.label}</button>
          ))}
        </nav>
      </div>
      <div className="p-6">
        {tab === 'dashboard' && dashboard && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Active Scenarios', value: dashboard.active_scenarios },
              { label: 'Simulations Run', value: dashboard.simulations_run },
              { label: 'Planning Confidence', value: dashboard.planning_confidence_score.toFixed(0) },
              { label: 'Enterprise Readiness', value: dashboard.enterprise_readiness_score.toFixed(0) },
              { label: 'Composite Risk', value: dashboard.composite_risk_score.toFixed(0) },
              { label: 'Constraint Violations', value: dashboard.constraint_violations },
              { label: 'Scenario Reuse Rate', value: `${dashboard.scenario_reuse_rate.toFixed(0)}%` },
              { label: 'Decision Acceptance', value: `${dashboard.decision_acceptance_rate.toFixed(0)}%` },
              { label: 'Avg Confidence', value: dashboard.avg_confidence.toFixed(0) },
              { label: 'Pending Recommendations', value: dashboard.pending_recommendations },
            ].map((m) => (
              <div key={m.label} className={card}><p className={`text-sm ${muted}`}>{m.label}</p><p className="mt-1 text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
        )}
        {tab === 'scenarios' && scenarios.map((s) => (
          <button key={s.id} type="button" onClick={() => setSelected(s)} className={`${card} mb-4 block w-full text-left ${selected?.id === s.id ? 'ring-2 ring-violet-500' : ''}`}>
            <h3 className="font-medium">{s.name}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{s.scenario_type.replace(/_/g, ' ')} · {s.status} · Risk {s.composite_risk_score.toFixed(0)}</p>
            {s.description && <p className="mt-2 text-sm">{s.description}</p>}
          </button>
        ))}
        {tab === 'twin' && twin && (
          <div className={card}>
            <h3 className="text-lg font-semibold">Enterprise Digital Twin</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {[
                { label: 'Demand', value: twin.demand_nodes },
                { label: 'Forecast', value: twin.forecast_nodes },
                { label: 'Inventory', value: twin.inventory_nodes },
                { label: 'Procurement', value: twin.procurement_nodes },
                { label: 'Supply', value: twin.supply_nodes },
                { label: 'Warehouse', value: twin.warehouse_nodes },
              ].map((n) => (
                <div key={n.label}><p className={`text-sm ${muted}`}>{n.label}</p><p className="text-xl font-semibold">{n.value}</p></div>
              ))}
            </div>
            <div className="mt-4">
              <p className={`text-sm font-medium ${muted}`}>Module Sync</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(twin.module_sync).map(([mod, status]) => (
                  <span key={mod} className="rounded-full bg-green-100 px-3 py-1 text-xs text-green-800 dark:bg-green-900 dark:text-green-200">{mod}: {status}</span>
                ))}
              </div>
            </div>
            {twin.bottleneck_nodes.length > 0 && (
              <div className="mt-4">
                <p className={`text-sm font-medium ${muted}`}>Bottlenecks</p>
                <ul className="mt-2 list-inside list-disc text-sm">{twin.bottleneck_nodes.map((b) => <li key={b}>{b}</li>)}</ul>
              </div>
            )}
          </div>
        )}
        {tab === 'simulation' && simResult && (
          <div className={card}>
            <h3 className="text-lg font-semibold">Simulation Results</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div><p className={`text-sm ${muted}`}>Revenue</p><p className="text-xl font-semibold">{fmtMoney(simResult.revenue)}</p></div>
              <div><p className={`text-sm ${muted}`}>Service Level</p><p className="text-xl font-semibold">{(simResult.service_level * 100).toFixed(0)}%</p></div>
              <div><p className={`text-sm ${muted}`}>Working Capital</p><p className="text-xl font-semibold">{fmtMoney(simResult.working_capital)}</p></div>
              <div><p className={`text-sm ${muted}`}>Composite Risk</p><p className="text-xl font-semibold">{simResult.composite_risk_score.toFixed(0)}</p></div>
            </div>
            <p className="mt-4 text-sm">{simResult.explanation}</p>
          </div>
        )}
        {tab === 'simulation' && !simResult && (
          <div className={card}><p className={muted}>Click Simulate to run a cross-functional scenario simulation.</p></div>
        )}
        {tab === 'constraints' && constraints.map((c) => (
          <div key={c.id} className={`${card} mb-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{c.type.replace(/_/g, ' ')} · {c.severity}</h3>
              {c.violated && <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800 dark:bg-red-900 dark:text-red-200">Violated</span>}
            </div>
            <p className="mt-2 text-sm">{c.description}</p>
            <p className="mt-1 text-sm">Mitigation: {c.mitigation}</p>
          </div>
        ))}
        {tab === 'risks' && risks.map((r) => (
          <div key={r.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{r.risk_type.replace(/_/g, ' ')} · {r.severity}</h3>
            <p className={`mt-1 text-sm ${muted}`}>Probability {(r.probability * 100).toFixed(0)}%</p>
            <p className="mt-2 text-sm">{r.business_impact}</p>
          </div>
        ))}
        {tab === 'recommendations' && recommendations.map((r) => (
          <div key={r.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{r.title}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{r.type.replace(/_/g, ' ')} · Confidence {(r.confidence * 100).toFixed(0)}%</p>
            <p className="mt-2 text-sm">{r.business_explanation}</p>
          </div>
        ))}
        {tab === 'templates' && templates.map((t) => (
          <div key={t.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{t.name}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{t.scenario_type.replace(/_/g, ' ')}</p>
            <p className="mt-2 text-sm">{t.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
